/**
 * TagInput — autocomplete + slug-based tag editor.
 *
 * Tags are identified by stable slug strings. The component:
 *  - Renders the value as a list of `TagBadge` (label resolved via i18n).
 *  - Suggests org-scoped tags from the registry, ranked by:
 *      1. defaults first  2. translated label match  3. alphabetical by label
 *  - On Enter with no exact match, sends the raw input to the parent via
 *    `onChange`. The backend canonicalises the new entry to a slug
 *    (existing match → registry slug, otherwise a new custom tag).
 *  - Hard cap at 10 tags (server-enforced too).
 *
 * Props:
 *   value     — array of slug strings currently on the candidate
 *   registry  — array of tag docs { slug, name, category, color, ... }
 *   onChange  — (newValue: string[]) => void (parent persists & refreshes)
 *   compact   — single-row mode (used in table cells)
 *   placeholder — input placeholder
 *   disabled  — disables editing (badges still rendered)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import TagBadge, { findTagBySlug, getCategoryFor } from './TagBadge';

const MAX_TAGS = 10;

const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

export default function TagInput({
    value = [],
    registry = [],
    onChange,
    compact = false,
    placeholder,
    disabled = false,
    autoFocus = false,
    'data-testid': testId = 'tag-input',
}) {
    const { t, i18n } = useTranslation();
    const [editing, setEditing] = useState(false);
    const [input, setInput] = useState('');
    const [highlight, setHighlight] = useState(0);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) inputRef.current.focus();
    }, [editing]);

    useEffect(() => {
        if (autoFocus) setEditing(true);
    }, [autoFocus]);

    useEffect(() => {
        if (!editing) return undefined;
        const onDocClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setEditing(false);
                setInput('');
            }
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [editing]);

    // Helper: resolved display label for a registry entry.
    const labelFor = (tag) => {
        if (!tag) return '';
        if (tag.slug && i18n.exists(`tags.${tag.slug}`)) return t(`tags.${tag.slug}`);
        return tag.name || tag.slug || '';
    };

    const valueSlugs = useMemo(() => new Set(value), [value]);

    const suggestions = useMemo(() => {
        const q = norm(input);
        return registry
            .filter((tg) => !valueSlugs.has(tg.slug))
            .filter((tg) => {
                if (!q) return true;
                const lbl = norm(labelFor(tg));
                const slug = norm(tg.slug);
                const name = norm(tg.name);
                return lbl.includes(q) || slug.includes(q) || name.includes(q);
            })
            .sort((a, b) => {
                if ((b.is_default ? 1 : 0) !== (a.is_default ? 1 : 0)) {
                    return (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0);
                }
                return labelFor(a).localeCompare(labelFor(b));
            })
            .slice(0, 12);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registry, input, valueSlugs, i18n.language]);

    const exactMatch = useMemo(() => {
        const q = norm(input);
        return suggestions.find((s) => norm(labelFor(s)) === q || norm(s.slug) === q);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [suggestions, input, i18n.language]);

    const canCreateNew = input.trim().length > 0 && !exactMatch;

    const addRaw = (rawText) => {
        // Parent persists & refreshes — we just compute the optimistic value.
        const trimmed = (rawText || '').trim();
        if (!trimmed) return;
        if (value.length >= MAX_TAGS) return;
        // Pass the raw input straight to the parent: backend canonicalises.
        onChange?.([...value, trimmed]);
        setInput('');
        setHighlight(0);
    };

    const addBySlug = (slug) => {
        if (!slug) return;
        if (valueSlugs.has(slug)) return;
        if (value.length >= MAX_TAGS) return;
        onChange?.([...value, slug]);
        setInput('');
        setHighlight(0);
    };

    const removeBySlug = (slug) => {
        onChange?.(value.filter((s) => s !== slug));
    };

    const handleKeyDown = (e) => {
        const max = suggestions.length + (canCreateNew ? 1 : 0);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (max > 0) setHighlight((h) => (h + 1) % max);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (max > 0) setHighlight((h) => (h - 1 + max) % max);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlight < suggestions.length) {
                addBySlug(suggestions[highlight].slug);
            } else if (canCreateNew) {
                addRaw(input);
            }
        } else if (e.key === 'Backspace' && !input && value.length > 0) {
            removeBySlug(value[value.length - 1]);
        } else if (e.key === 'Escape') {
            setInput('');
            setEditing(false);
        }
    };

    const reachedMax = value.length >= MAX_TAGS;

    return (
        <div
            ref={containerRef}
            className={`relative inline-flex flex-wrap items-center gap-1 ${compact ? '' : 'min-h-[36px] w-full'}`}
            data-testid={testId}
        >
            {value.map((slug) => (
                <TagBadge
                    key={slug}
                    slug={slug}
                    registry={registry}
                    category={getCategoryFor(slug, registry)}
                    onRemove={disabled ? undefined : removeBySlug}
                />
            ))}

            {!disabled && !reachedMax && !editing && (
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-dashed border-[#9CA3AF] text-[#4B5563] hover:bg-[#F3F4F6] text-xs font-medium"
                    data-testid={`${testId}-add-btn`}
                >
                    <Plus className="w-3 h-3" />
                    {t('tags.add')}
                </button>
            )}

            {!disabled && reachedMax && (
                <span className="text-[10px] text-[#9CA3AF] italic">{t('tags.max_reached')}</span>
            )}

            {editing && (
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => { setInput(e.target.value); setHighlight(0); }}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder || t('tags.placeholder')}
                        className="min-w-[160px] text-xs px-2 py-1 border border-[#D1D5DB] rounded-md focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                        data-testid={`${testId}-text`}
                    />

                    {(suggestions.length > 0 || canCreateNew) && (
                        <ul
                            className="absolute z-50 mt-1 left-0 min-w-[240px] max-h-60 overflow-auto bg-white border border-[#E5E7EB] rounded-md shadow-lg py-1"
                            data-testid={`${testId}-suggestions`}
                        >
                            {suggestions.map((s, i) => (
                                <li
                                    key={s.slug}
                                    onMouseDown={(e) => { e.preventDefault(); addBySlug(s.slug); }}
                                    onMouseEnter={() => setHighlight(i)}
                                    className={`px-2 py-1.5 cursor-pointer flex items-center justify-between gap-2 text-xs ${i === highlight ? 'bg-[#F3F4F6]' : ''}`}
                                    data-testid={`${testId}-suggestion-${s.slug}`}
                                >
                                    <TagBadge slug={s.slug} registry={registry} category={s.category} />
                                    <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">
                                        {t(`tags.cat_${s.category}`, { defaultValue: s.category })}
                                    </span>
                                </li>
                            ))}
                            {canCreateNew && (
                                <li
                                    onMouseDown={(e) => { e.preventDefault(); addRaw(input); }}
                                    onMouseEnter={() => setHighlight(suggestions.length)}
                                    className={`px-2 py-1.5 cursor-pointer flex items-center gap-1 text-xs border-t border-[#F3F4F6] ${highlight === suggestions.length ? 'bg-[#F3F4F6]' : ''}`}
                                    data-testid={`${testId}-create-new`}
                                >
                                    <Plus className="w-3 h-3 text-[#002FA7]" />
                                    <span>{t('tags.create_new', { name: input })}</span>
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
